import React, { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons"
import { COLORS } from "../../utilities/Constants"
import { searchEntites } from "../../store/actions/agendaActions"

// Autocomplete client (Entite). Recherche serveur (GET /api/entites/search) par
// label / nom / prénom et permet de sélectionner une entité existante. Le texte
// libre reste autorisé (un Rdv peut viser un client pas encore enregistré).
//
// Props :
//   value     texte courant de l'input
//   onChange  (texte) — frappe libre
//   onSelect  (entite) — une entité de la liste est choisie
//   selected  bool — true quand `value` correspond à une entité DB sélectionnée
//   placeholder
//   inputRef  ref posé sur l'input (chaînage clavier des formulaires)
//   nextRef   champ suivant : focus après sélection ou Enter sans liste
const ClientAutocomplete = ({
	value,
	onChange,
	onSelect,
	selected,
	placeholder,
	inputRef = null,
	nextRef = null,
}) => {
	const dispatch = useDispatch()
	const itemRefs = useRef([])
	const [open, setOpen] = useState(false)
	const [highlight, setHighlight] = useState(0)
	const [results, setResults] = useState([])

	// Recherche serveur débouncée. Le flag `active` ignore les réponses périmées
	// (une frappe plus récente a relancé l'effet entre-temps).
	useEffect(() => {
		const q = (value || "").trim()
		if (!q) {
			setResults([])
			return
		}
		let active = true
		const t = setTimeout(() => {
			dispatch(searchEntites(q)).then((list) => {
				if (active) setResults((list || []).slice(0, 8))
			})
		}, 200)
		return () => {
			active = false
			clearTimeout(t)
		}
	}, [value, dispatch])

	const visible = open && results.length > 0

	// Garde l'élément surligné visible quand on navigue au clavier (liste qui
	// défile au-delà de la hauteur max).
	useEffect(() => {
		if (visible && itemRefs.current[highlight]) {
			itemRefs.current[highlight].scrollIntoView({ block: "nearest" })
		}
	}, [highlight, visible])

	const choisir = (entite) => {
		onSelect(entite)
		setOpen(false)
		if (nextRef && nextRef.current) nextRef.current.focus()
	}

	const onKeyDown = (e) => {
		if (!visible) {
			// Enter sans liste ouverte → champ suivant (chaînage clavier).
			if (e.key === "Enter" && nextRef && nextRef.current) {
				e.preventDefault()
				nextRef.current.focus()
			}
			return
		}
		if (e.key === "ArrowDown") {
			e.preventDefault()
			setHighlight((h) => Math.min(h + 1, results.length - 1))
		} else if (e.key === "ArrowUp") {
			e.preventDefault()
			setHighlight((h) => Math.max(h - 1, 0))
		} else if (e.key === "Enter") {
			e.preventDefault()
			choisir(results[highlight])
		} else if (e.key === "Escape") {
			// Liste ouverte : Échap la ferme seulement (consommé, comme
			// FilteredSelectV2) ; liste fermée : laissé à la page.
			e.preventDefault()
			setOpen(false)
		}
	}

	return (
		<div style={st.wrap}>
			<input
				ref={inputRef}
				type="text"
				value={value}
				placeholder={placeholder}
				autoComplete="off"
				role="combobox"
				aria-expanded={visible}
				onChange={(e) => {
					const v = e.target.value
					onChange(v)
					setOpen(true)
					setHighlight(0)
				}}
				onFocus={() => setOpen(true)}
				// délai pour laisser passer le clic sur un item avant de fermer
				onBlur={() => setTimeout(() => setOpen(false), 120)}
				onKeyDown={onKeyDown}
				style={selected ? { ...st.input, paddingRight: 36 } : st.input}
			/>

			{selected && (
				<FontAwesomeIcon
					icon={faCheckCircle}
					style={st.check}
					title="Client enregistré sélectionné"
				/>
			)}

			{visible && (
				<div style={st.menu}>
					{results.map((entite, i) => (
						<button
							key={entite.id}
							ref={(el) => (itemRefs.current[i] = el)}
							type="button"
							// onMouseDown : se déclenche avant le blur de l'input
							onMouseDown={(e) => {
								e.preventDefault()
								choisir(entite)
							}}
							onMouseEnter={() => setHighlight(i)}
							style={{
								...st.item,
								backgroundColor:
									i === highlight
										? COLORS.accent
										: "transparent",
							}}
						>
							<span
								style={{
									...st.itemLabel,
									color:
										i === highlight ? "#fff" : COLORS.text,
								}}
							>
								{entite.label || entite.display}
							</span>
							<span
								style={{
									...st.itemMeta,
									color:
										i === highlight
											? "rgba(255,255,255,0.85)"
											: COLORS.textLight,
								}}
							>
								{entite.type_label || ""}
								{entite.telephone
									? ` · ${entite.telephone}`
									: ""}
							</span>
						</button>
					))}
				</div>
			)}
		</div>
	)
}

const st = {
	wrap: { position: "relative" },
	check: {
		position: "absolute",
		right: 12,
		top: "50%",
		transform: "translateY(-50%)",
		color: COLORS.success,
		fontSize: 15,
		pointerEvents: "none",
	},
	input: {
		width: "100%",
		height: 38,
		padding: "0 12px",
		borderRadius: 10,
		border: `1px solid ${COLORS.border}`,
		fontSize: 14,
		color: COLORS.text,
		fontFamily: "inherit",
		backgroundColor: COLORS.surface,
		boxSizing: "border-box",
	},
	menu: {
		position: "absolute",
		top: "calc(100% + 4px)",
		left: 0,
		right: 0,
		backgroundColor: COLORS.surface,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 10,
		boxShadow: "0 12px 24px rgba(28, 25, 23, 0.12)",
		zIndex: 10,
		overflow: "hidden",
		maxHeight: 240,
		overflowY: "auto",
	},
	item: {
		display: "flex",
		flexDirection: "column",
		gap: 2,
		width: "100%",
		padding: "8px 12px",
		border: "none",
		borderBottom: `1px solid ${COLORS.bg}`,
		textAlign: "left",
		cursor: "pointer",
		fontFamily: "inherit",
	},
	itemLabel: { fontSize: 14, fontWeight: 600, color: COLORS.text },
	itemMeta: { fontSize: 12, color: COLORS.textLight },
}

export default ClientAutocomplete
